import { collect, Collection } from 'collect.js';
import { app } from '@/main';
import { NodeModel } from '@/modules/workflow/models';
import { RouteProvider } from '@/modules/navigation/providers/route.provider';

export class RouteModel extends NodeModel {
  public header = {
    navBar: {
      visible: true,
      itemVisible: true,
      moveTo: null,
      lockPreviousRoutes: false,
      disableBackButton: false,
      disableNextButton: false,
      bypassRoute: false,
      hideNavigation: false,
      hideSteps: false,
      disableNavDrawer: false,
      skipRoute: false, // boolean | string for a fixed skip target
    },
  };

  declare public parent?: this;

  public collection: Collection<this> = collect();

  public navigated = false;

  public guarded = true;

  public constructor(props: any, public provider: RouteProvider) {
    // @ts-ignore
    super(props, provider);
    Object.assign(this, props);
  }

  private static get hiddenPages() : Array<string> {
    return app.config.globalProperties.$workflowManager.compiler.getState?.hiddenPages || [];
  }

  private static get data() : any {
    return app.config.globalProperties.$workflowManager?.compiler?.getState?.data;
  }

  public get enabled() {
    if (typeof this.header?.navBar?.itemVisible === 'boolean') { return this.header?.navBar?.itemVisible; }

    if (typeof this.header?.navBar?.itemVisible === 'string' && !!RouteModel.data) {
      try {
        // eslint-disable-next-line no-new-func
        return new Function(`return ${this.header?.navBar?.itemVisible}`).call(RouteModel.data);
      } catch (e) {
        return false;
      }
    }

    return true;
  }

  public get bypassRoute() {
    if (typeof this.header?.navBar?.bypassRoute === 'boolean') {
      return this.header?.navBar?.bypassRoute;
    }

    if (typeof this.header?.navBar?.bypassRoute === 'string' && !!RouteModel.data) {
      try {
        // eslint-disable-next-line no-new-func
        return new Function(`return ${this.header?.navBar?.bypassRoute}`).call(RouteModel.data);
      } catch (e) {
        return false;
      }
    }

    return false;
  }

  public get isBypassEnabled() {
    return this.bypassRoute || RouteModel.hiddenPages?.includes(this.title);
  }

  public get skipRoute(): boolean | string {
    return this.header?.navBar?.skipRoute;
  }

  public get isNavigationHidden() {
    return !!this.header?.navBar?.hideNavigation;
  }

  public get isStepsHidden() {
    return !!this.header?.navBar?.hideSteps;
  }

  public get isNavDrawerDisabled() {
    return !!this.header?.navBar?.disableNavDrawer;
  }

  // Localization needed
  get title(): string {
    return this.routeName;
  }

  get isRouteType(): boolean {
    return this.type.toLowerCase() === 'route';
  }

  get fullPath() {
    const parentPath = this.parent ? `/${this.parent.routeName}` : '';

    return `${parentPath}/${this.routeName}`;
  }

  get isGuarded() {
    return typeof this.guarded === 'undefined' || this.guarded;
  }

  public async navigate() {
    const prevRoute = this.provider.activeRoute;

    // Case when we want to collapse previous child's parent first, and then activate (and expand if needed) to the current one
    if (prevRoute?.parent && !this.parent && prevRoute.parent !== this) {
      try {
        prevRoute.parent.isExpanded = false;

        await prevRoute.parent.animate();
      } catch (e) {
        console.error('NavBar animation failed');
      }
    } else if (prevRoute !== this && prevRoute?.isExpanded && prevRoute !== this.parent) {
      prevRoute.isExpanded = false;

      await prevRoute.animate();
    }

    if (this.isExpandable) {
      this.toggleExpand();
    }

    this.provider.deactivateAll();
    this.active = true;
    this.navigated = true;
    this.provider.setActiveRoute(this);

    if (!this.header?.navBar?.itemVisible) { return; }

    const animatedRoute = this.header?.navBar?.moveTo ? this.provider.resolveRoute(this.header?.navBar?.moveTo) : this;

    await animatedRoute.animate();
    this.provider.emit('animationComplete', { route: this });
  }

  // TODO: obsolete, was used for the old NavBar animation.
  private animate(route: RouteModel = this) {
    return Promise.resolve();
  }
}
